import { useCallback, useEffect, useState } from "react";

import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";

import { ClientDebtTable } from "./components/ClientDebtTable";
import { DateSelector } from "./components/DateSelector";
import { DebtSummaryCard } from "./components/DebtSummaryCard";
import { useAvailableSnapshotDates } from "./hooks/useAvailableSnapshotDates";
import { useDailyDebtSnapshot } from "./hooks/useDailyDebtSnapshot";
import type { SortColumn, SortDirection } from "./types";

const PAGE_SIZE = 50;

export default function DailyDebtSnapshotsPage() {
  const {
    dates,
    latestDate,
    loading: datesLoading,
  } = useAvailableSnapshotDates();

  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortColumn, setSortColumn] = useState<SortColumn>("debt_amount");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  // Initialize with latest date when available
  useEffect(() => {
    if (latestDate && !selectedDate) {
      setSelectedDate(latestDate);
    }
  }, [latestDate, selectedDate]);

  const {
    summary,
    previousDayDebt,
    clients,
    totalClients,
    totalPages,
    loading: dataLoading,
  } = useDailyDebtSnapshot({
    date: selectedDate,
    page: currentPage,
    pageSize: PAGE_SIZE,
    searchTerm,
    sortColumn,
    sortDirection,
  });

  const handleDateChange = useCallback((date: string) => {
    setSelectedDate(date);
    setCurrentPage(1);
  }, []);

  const handleSearchChange = useCallback((term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
  }, []);

  const handleSortChange = useCallback(
    (column: SortColumn) => {
      setSortDirection((prev) =>
        sortColumn === column ? (prev === "asc" ? "desc" : "asc") : "desc"
      );
      setSortColumn(column);
      setCurrentPage(1);
    },
    [sortColumn]
  );

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  return (
    <div className="container mx-auto space-y-6 py-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">
          Historial de Deuda
        </h1>
        <p className="text-muted-foreground">
          Visualiza el estado de la deuda en cualquier fecha histórica
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Seleccionar Fecha</CardTitle>
        </CardHeader>
        <CardContent>
          <DateSelector
            selectedDate={selectedDate}
            onDateChange={handleDateChange}
            availableDates={dates}
            loading={datesLoading}
          />
        </CardContent>
      </Card>

      <DebtSummaryCard
        summary={summary}
        previousDayDebt={previousDayDebt}
        totalClients={totalClients}
        loading={dataLoading && !summary}
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Detalle por Cliente</CardTitle>
        </CardHeader>
        <CardContent>
          <ClientDebtTable
            clients={clients}
            totalDebt={summary?.total_debt || 0}
            totalClients={totalClients}
            totalPages={totalPages}
            currentPage={currentPage}
            loading={dataLoading}
            searchTerm={searchTerm}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSearchChange={handleSearchChange}
            onSortChange={handleSortChange}
            onPageChange={handlePageChange}
          />
        </CardContent>
      </Card>
    </div>
  );
}
